'use client';

import { useEffect, useState } from 'react';
import type Swiper from 'swiper';
import { Button } from 'rizzui';

type TeamSliderElement = HTMLElement & { swiper?: Swiper };

function SliderArrow({ direction }: { direction: 'previous' | 'next' }) {
  const isPrevious = direction === 'previous';

  return (
    <svg
      aria-hidden="true"
      className="block shrink-0 bg-transparent"
      xmlns="http://www.w3.org/2000/svg"
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
    >
      <path
        d={
          isPrevious
            ? 'M11.3481 7.47314L5.25879 13.2856L11.3481 19.0981'
            : 'M15.2234 7.47314L21.3126 13.2856L15.2234 19.0981'
        }
        stroke="#1a1a1a"
        strokeWidth="1.66071"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={isPrevious ? 'M21.3124 13.2856H5.53564' : 'M5.259 13.2856H21.0358'}
        stroke="#1a1a1a"
        strokeWidth="1.66071"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getTeamSlider() {
  return document.querySelector<TeamSliderElement>('.slider-team')?.swiper;
}

export function AboutTeamNavigation() {
  const [atBeginning, setAtBeginning] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    let frame: number;
    let slider: Swiper | undefined;
    let cancelled = false;

    const sync = () => {
      if (!slider) return;
      setAtBeginning(slider.isLocked || slider.isBeginning);
      setAtEnd(slider.isLocked || slider.isEnd);
    };
    const connect = () => {
      slider = getTeamSlider();
      if (!slider) {
        if (!cancelled) frame = window.requestAnimationFrame(connect);
        return;
      }
      slider.on('slideChange', sync);
      slider.on('reachBeginning', sync);
      slider.on('reachEnd', sync);
      slider.on('fromEdge', sync);
      slider.on('lock', sync);
      slider.on('unlock', sync);
      sync();
    };

    frame = window.requestAnimationFrame(connect);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      slider?.off('slideChange', sync);
      slider?.off('reachBeginning', sync);
      slider?.off('reachEnd', sync);
      slider?.off('fromEdge', sync);
      slider?.off('lock', sync);
      slider?.off('unlock', sync);
    };
  }, []);

  return (
    <div className="team-slider-controls absolute right-5 top-[70px] z-[5] !mt-0 flex gap-2.5">
      <Button
        type="button"
        variant="text"
        className="swiper-btn-prev team-swiper-btn-prev !flex !h-10 !w-10 !min-w-10 items-center justify-center !rounded-full !border-0 !bg-white !text-[#1a1a1a] shadow-none hover:!bg-white focus:!outline-none active:!bg-white disabled:!flex disabled:cursor-not-allowed disabled:opacity-[0.45] [&&&]:!p-0 [&>svg]:!h-[27px] [&>svg]:!w-[27px] [&>svg]:!min-w-[27px] [&>svg]:bg-transparent"
        aria-label="Previous team member"
        disabled={atBeginning}
        onClick={() => getTeamSlider()?.slidePrev()}
      >
        <SliderArrow direction="previous" />
      </Button>
      <Button
        type="button"
        variant="text"
        className="swiper-btn-next team-swiper-btn-next !flex !h-10 !w-10 !min-w-10 items-center justify-center !rounded-full !border-0 !bg-white !text-[#1a1a1a] shadow-none hover:!bg-white focus:!outline-none active:!bg-white disabled:!flex disabled:cursor-not-allowed disabled:opacity-[0.45] [&&&]:!p-0 [&>svg]:!h-[27px] [&>svg]:!w-[27px] [&>svg]:!min-w-[27px] [&>svg]:bg-transparent"
        aria-label="Next team member"
        disabled={atEnd}
        onClick={() => getTeamSlider()?.slideNext()}
      >
        <SliderArrow direction="next" />
      </Button>
    </div>
  );
}
